import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { dateFormat } from '../../utils/formattedDate';
import { getUserCounts, acceleratorListApi, suspendUserApi, restoreUserApi, updateUserDetailApi } from '../../api/usersApi';

import { PageTabs } from '../../components/PageTabs';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import HeaderInfo from '../../components/HeaderInfo';
import Notification from '../../components/Notification';
import AddNewVc from '../../components/VCs/AddNewVc';
import EditUser from '../../components/EditSlider/EditUser';
import ActiveAccelerators from './ActiveAccelerators';
import AddNewAccelerator from '../../components/Accelerators/AddNewAccelerator';
import SingleSuspendPopup from '../../components/SingleSuspendPopup';
import MultipleSuspendPopup from '../../components/MultipleSuspendPopup';
import NotCompletedAccelerator from './NotCompleted';
import SuspendedAccelerators from './SuspendedAccelerator';

import '../../assets/css/Accelerators.css'
import '../../assets/css/Popup.css'

const Accelerators = () => {
    const location = useLocation();
    const userEditRef = useRef();
    const addAcceleratorsRef = useRef();
    const suspendAcceleratorsRef = useRef();
    const suspendMultipleAcceleratorsRef = useRef();
    const debounceRef = useRef(null);

    const [acceleratorsCount, setAcceleratorsCount] = useState([]);
    const [activePage, setActivePage] = useState(`Active Accelerators (${acceleratorsCount[1]})`);
    const [ProfileTabs, setProfileTabs] = useState([]);
    const [menuActive, setMenuActive] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [isEditAccelerators, setEditAccelerators] = useState(false)
    const [showAddNewAcceleratorsPopup, setShowAddNewAcceleratorsPopup] = useState(false);
    const [selectedAccelerator, setSelectAccelerator] = useState({})
    const [isSuspendLoading, setIsSuspendLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true)
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [acceleratorsList, setAcceleratorsList] = useState([]);
    const [totalPage, setTotalPage] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAcceleratorsSuspendPopup, setShowAcceleratorsSuspendPopup] = useState(false);
    const [showAcceleratorsMultipleSuspendPopup, setShowAcceratorsMultipleSuspendPopup] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [toggleSelect, setToggleSelect] = useState(false);
    const [reason, setReason] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [personalEmail, setPersonalEmail] = useState(selectedAccelerator?.email || '')
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [filterAccelerator, setFilterAccelerator] = useState({
        industry: [],
        startupStage: [],
        teamCount: [],
        plan: [],
        lastActiveDate: '',
        nextBillingDate: '',
        registedDate: '',
        suspendedDate: ''
    })
    const [addAcceleratorErrorMessage, setAddAcceleratorErrorMessage] = useState({
        name: '',
        email: '',
    })

    const currentStatus = useMemo(() => {
        const path = location.pathname.toLowerCase();
        if (path.includes('/notcompleted')) return 'notCompleted';
        if (path.includes('/suspended')) return 'suspended';
        return 'active';
    }, [location.pathname]);

    useEffect(() => {
        const statusLabelMap = {
            active: `Active Accelerators (${acceleratorsCount[1] || 0})`,
            notCompleted: `Not Completed (${acceleratorsCount[2] || 0})`,
            suspended: `Suspended (${acceleratorsCount[3] || 0})`
        };
        setActivePage(statusLabelMap[currentStatus]);

        setProfileTabs([
            { name: statusLabelMap['active'], href: '/accelerators/active' },
            { name: statusLabelMap['notCompleted'], href: '/accelerators/notcompleted' },
            { name: statusLabelMap['suspended'], href: '/accelerators/suspended' }
        ]);

        setSelectedRows([]);
        setIsAllSelected(false);
    }, [currentStatus, acceleratorsCount]);

    useEffect(() => {
        fetchAcceleratorCount();
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setIsLoadingData(true);
        if (debounceRef?.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            fetchAccelerators();
        }, 1500);

        return () => clearTimeout(debounceRef.current);
    }, [currentPage, filterAccelerator, searchQuery, location?.pathname]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filterAccelerator, searchQuery]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suspendAcceleratorsRef?.current && !suspendAcceleratorsRef?.current.contains(event.target)) {
                setShowAcceleratorsSuspendPopup(false);
            }
            if (suspendMultipleAcceleratorsRef?.current && !suspendMultipleAcceleratorsRef?.current.contains(event.target)) {
                setShowAcceratorsMultipleSuspendPopup(false);
            }
            if (userEditRef?.current && !userEditRef?.current.contains(event.target)) {
                setEditAccelerators(false);
            }
            if (addAcceleratorsRef?.current && !addAcceleratorsRef?.current.contains(event.target)) {
                setShowAddNewAcceleratorsPopup(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const fetchAcceleratorCount = async () => {
        try {
            const res = await getUserCounts({ type: 4 });
            const countsByStatus = {
                1: 0,
                2: 0,
                3: 0,
            };
            res.forEach(item => {
                if (countsByStatus.hasOwnProperty(item?.status)) {
                    countsByStatus[item?.status] = item?.count;
                }
            });
            setAcceleratorsCount(countsByStatus);
        } catch (error) {
            console.log(error);
        }
    };

    const handleEditClick = async (row) => {
        try {
            setEditAccelerators(true);
            setSelectAccelerator(row);
        } catch (error) {
            console.error('Failed to fetch user detail on edit:', error);
        }
    };

    const acceleratorApi = async () => {
        const status = currentStatus;

        const data = await acceleratorListApi({
            status,
            page: currentPage || 1,
            search: searchQuery?.toLowerCase() || undefined,
            industry: status !== 'notCompleted' ? filterAccelerator?.industry?.map((item) => item?.toString()) : undefined,
            startupStage: status !== 'notCompleted' ? filterAccelerator?.startupStage?.map((item) => item?.toString()) : undefined,
            teamCount: status !== 'notCompleted' ? filterAccelerator?.teamCount?.map((item) => item?.toString()) : undefined,
            plan: status !== 'notCompleted' ? filterAccelerator?.plan?.map((item) => item?.toString()) : undefined,
            registeredStart: (filterAccelerator?.registedDate?.from !== '' && filterAccelerator?.registedDate?.from !== undefined && filterAccelerator?.registedDate?.from) ? dateFormat(filterAccelerator?.registedDate?.from) : undefined,
            registeredEnd: (filterAccelerator?.registedDate?.to !== '' && filterAccelerator?.registedDate?.to !== undefined && filterAccelerator?.registedDate?.to) ? dateFormat(filterAccelerator?.registedDate?.to) : undefined,
            suspendedStart: status !== 'notCompleted' ? ((filterAccelerator?.suspendedDate?.from !== '' && filterAccelerator?.suspendedDate?.from !== undefined && filterAccelerator?.suspendedDate?.from) ? dateFormat(filterAccelerator?.suspendedDate?.from) : undefined) : undefined,
            suspendedEnd: status !== 'notCompleted' ? ((filterAccelerator?.suspendedDate?.to !== '' && filterAccelerator?.suspendedDate?.to !== undefined && filterAccelerator?.suspendedDate?.to) ? dateFormat(filterAccelerator?.suspendedDate?.to) : undefined) : undefined,
        }, setIsLoadingData);
        return data
    }

    const fetchAccelerators = async () => {
        setIsLoading(false);
        try {
            setIsLoading(true)
            setIsLoadingData(true)

            const data = await acceleratorApi()
            if (data?.list?.length <= 0) {
                setIsLoadingData(false)
            }
            setAcceleratorsList(data?.list);
            setTotalPage(data?.totalPage)
            setIsLoading(false)
        } catch (error) {
            console.log(error);
            setIsLoading(false)
            setIsLoadingData(false)
        }
    };

    const suspendUser = async (reason, userId, showAcceleratorsMultipleSuspendPopup = false) => {
        try {
            await suspendUserApi({
                reason,
                userIds: showAcceleratorsMultipleSuspendPopup ? userId : [userId],
            });
            return true;
        } catch (error) {
            console.error('API error:', error.response?.data || error.message);
            return false;
        }
    };

    const restoreUser = async (reason, userId, showAcceleratorsMultipleSuspendPopup = false) => {
        try {
            await restoreUserApi({
                reason,
                userIds: showAcceleratorsMultipleSuspendPopup ? userId : [userId],
            });
            return true;
        } catch (error) {
            console.error('API error:', error.response?.data || error.message);
            return false;
        }
    }

    const updateUserData = async (userId, email) => {
        try {
            await updateUserDetailApi({
                userId,
                data: { email },
            });
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    const handleUpdate = async ({ userId, email }) => {
        setIsSaveLoading(false)
        setShowNotification(false);
        try {
            setIsSaveLoading(true);
            const success = await updateUserData(userId, email);

            if (success) {
                const data = await acceleratorApi()
                if (data) {
                    setIsLoadingData(false)
                    setAcceleratorsList(data?.list);
                    setNotificationMessage('Your changes have been successfully saved.')
                    setShowNotification(true);
                    setTimeout(() => setShowNotification(false), 8000);
                    setEditAccelerators(false)
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsSaveLoading(false);
        }
    };

    const handleUpdateUser = (changedPersonalEmail) => {
        handleUpdate({
            userId: selectedAccelerator?.userId,
            email: changedPersonalEmail,
        });
    };

    const handleUpdateTeamUser = (member) => {
        handleUpdate({
            userId: member?.userId,
            email: member?.email,
        });
    };


    const handleSuspendUser = async (reason, profileData) => {
        setIsSuspendLoading(false);
        setShowNotification(false);
        const notificationText = `${profileData?.name || ""} was suspended from Ivycorn.`;
        try {
            setIsSuspendLoading(true);
            const success = await suspendUser(reason, profileData?.userId);
            if (success) {
                const data = await acceleratorApi()
                await fetchAcceleratorCount()
                if (data) {
                    setIsLoadingData(false)
                    setAcceleratorsList(data?.list);
                    setTotalPage(data?.totalPage)
                    setShowNotification(true);
                    setNotificationMessage(notificationText);
                    setTimeout(() => {
                        setShowNotification(false);
                    }, 8000);
                    setIsSuspendLoading(false)
                    setEditAccelerators(false)
                    if (data?.list?.length === 0) {
                        setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                    }
                }
                return true
            } else {
                setIsSuspendLoading(false)
            }
        } catch (error) {
            console.log(error);
            setIsSuspendLoading(false)
        }
    }

    const handleActionUser = async (reason) => {
        let status = currentStatus;
        setShowNotification(false);
        setErrorMessage('');
        let notificationText = '';
        let success = false;
        if (!reason.trim()) {
            setErrorMessage(
                status !== "suspended"
                    ? "Please enter reason for suspension."
                    : "Please enter reason for restore."
            );
            return;
        }

        setIsSuspendLoading(true)
        if (status === "suspended") {
            if (showAcceleratorsSuspendPopup) {
                notificationText = `${selectedAccelerator?.name || ""} was restored from Ivycorn.`;
            } else {
                notificationText = `${selectedRows?.length} accelerators were restored from Ivycorn.`
            }
            success = await restoreUser(reason, showAcceleratorsSuspendPopup ? selectedAccelerator?.userId : selectedRows, showAcceleratorsMultipleSuspendPopup);
        } else {
            if (showAcceleratorsSuspendPopup) {
                notificationText = `${selectedAccelerator?.name || ""} was suspended from Ivycorn.`;
            } else {
                notificationText = `${selectedRows?.length} accelerators were suspended from Ivycorn.`
            }
            success = await suspendUser(reason, showAcceleratorsSuspendPopup ? selectedAccelerator?.userId : selectedRows, showAcceleratorsMultipleSuspendPopup);
        }
        if (success) {
            const data = await acceleratorApi()
            await fetchAcceleratorCount()
            if (data) {
                setAcceleratorsList(data?.list);
                setTotalPage(data?.totalPage)
                setShowNotification(true);
                setNotificationMessage(notificationText);
                setToggleSelect(!toggleSelect);
                setTimeout(() => {
                    setShowNotification(false);
                }, 8000);
                setShowAcceratorsMultipleSuspendPopup(false)
                setShowAcceleratorsSuspendPopup(false)
                setReason('')
                if (data?.list?.length === 0) {
                    setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                }
            }
            setIsSuspendLoading(false)
        } else {
            setIsSuspendLoading(false)
        }
    };

    const LazyImage = ({ src, alt }) => {
        const [isVisible, setIsVisible] = useState(false);
        const imgRef = useRef();

        useEffect(() => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.disconnect();
                    }
                },
                { threshold: 0.1 }
            );

            if (imgRef.current) {
                observer.observe(imgRef.current);
            }

            return () => {
                if (imgRef.current) {
                    observer.unobserve(imgRef.current);
                }
            };
        }, []);

        return (
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                loading="lazy"
            />
        );
    };

    const acceleratorUserList = useMemo(() => {
        return acceleratorsList
    }, [acceleratorsList])

    const acceleratorProps = {
        setShowAddNewAcceleratorsPopup,
        setSelectAccelerator,
        isLoading,
        isLoadingData,
        acceleratorsList: acceleratorUserList,
        totalPage,
        setEditAccelerators,
        setIsLoadingData,
        currentPage,
        setSearchQuery,
        searchQuery,
        setFilterAccelerator,
        filterAccelerator,
        setShowAcceleratorsSuspendPopup,
        setShowAcceratorsMultipleSuspendPopup,
        selectedRows,
        setSelectedRows,
        isAllSelected,
        setIsAllSelected,
        toggleSelect,
        setErrorMessage,
        setCurrentPage,
        setReason,
        LazyImage,
        setAddAcceleratorErrorMessage
    };

    const getAcceleratorComponent = (Component) => <Component {...acceleratorProps} />;

    return (
        <div className='main-wrapper'>
            <Header activePage="accelerators" menuActive={menuActive} setMenuActive={setMenuActive} />
            <div className="main-content">
                <div className={`content-wrapper`}>
                    <HeaderInfo title='Accelerators' description={
                        activePage.startsWith('Active')
                            ? `You have ${acceleratorsCount[1] || 0} active accelerators.`
                            : activePage.startsWith('Not Completed')
                                ? `You have ${acceleratorsCount[2] || 0} not completed accelerators.`
                                : activePage.startsWith('Suspended')
                                    ? `You have ${acceleratorsCount[3] || 0} suspended accelerators.`
                                    : ''
                    } />
                    <PageTabs tabs={ProfileTabs} activePage={activePage} setActivePage={(tabName) => {
                        setActivePage(tabName);
                        setFilterAccelerator({
                            industry: [],
                            startupStage: [],
                            teamCount: [],
                            plan: [],
                            lastActiveDate: '',
                            nextBillingDate: '',
                            registedDate: '',
                            suspendedDate: ''
                        });
                        setSearchQuery('')
                    }} />
                    <div className="acceleratorsContent">
                        <Routes basename="/accelerators">
                            <Route path="/" element={getAcceleratorComponent(ActiveAccelerators)} />
                            <Route path="/active" element={getAcceleratorComponent(ActiveAccelerators)} />
                            <Route path="/notcompleted" element={getAcceleratorComponent(NotCompletedAccelerator)} />
                            <Route path="/suspended" element={getAcceleratorComponent(SuspendedAccelerators)} />
                        </Routes>
                    </div>
                </div>
                <Footer menuActive={menuActive} />
            </div>

            {
                showNotification &&
                <Notification showNotification={showNotification} notificationMessage={notificationMessage} setShowNotification={setShowNotification} />
            }
            {
                showAddNewAcceleratorsPopup &&
                <AddNewAccelerator
                    ref={addAcceleratorsRef}
                    isOpen={showAddNewAcceleratorsPopup}
                    onClose={() => setShowAddNewAcceleratorsPopup(false)}
                    setShowAddNewAcceleratorsPopup={setShowAddNewAcceleratorsPopup}
                    setShowNotification={setShowNotification}
                    setNotificationMessage={setNotificationMessage}
                    setAddAcceleratorErrorMessage={setAddAcceleratorErrorMessage}
                    addAcceleratorErrorMessage={addAcceleratorErrorMessage}
                />
            }
            {
                showAcceleratorsSuspendPopup &&
                <SingleSuspendPopup
                    isOpen={showAcceleratorsSuspendPopup}
                    onClose={() => { setShowAcceleratorsSuspendPopup(false) }}
                    title={`Are you sure you want to ${activePage == `Suspended (${acceleratorsCount[3] || 0})` ? 'restore' : 'suspend'} ${selectedAccelerator?.name}?`}
                    description={`${activePage === `Suspended (${acceleratorsCount[3] || 0})` ? "Restoring this accelerator will reinstate their access to Ivycorn, allowing them to continue using the service." : "Suspending this accerator means they will be permanently suspended from using Ivycorn."}`}
                    ref={suspendAcceleratorsRef}
                    status={activePage === `Suspended (${acceleratorsCount[3] || 0})` && 'suspended'}
                    handleActionUser={handleActionUser}
                    reason={reason}
                    setReason={setReason}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    isSuspendLoading={isSuspendLoading}
                />
            }
            {
                showAcceleratorsMultipleSuspendPopup &&
                <MultipleSuspendPopup
                    ref={suspendMultipleAcceleratorsRef}
                    isOpen={showAcceleratorsMultipleSuspendPopup}
                    onClose={() => { setShowAcceratorsMultipleSuspendPopup(false) }}
                    title={`Are you sure you want to ${activePage === `Suspended (${acceleratorsCount[3] || 0})` ? 'restore' : 'suspend'} these ${selectedRows?.length} accerators?`}
                    description={`${activePage === `Suspended (${acceleratorsCount[3] || 0})` ? "Restoring these accelerators will reinstate their access to Ivycorn, allowing them to continue using the service." : "Suspending these accerators means they will be permanently suspended from using Ivycorn."}`}
                    handleActionUser={handleActionUser}
                    reason={reason}
                    setReason={setReason}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    status={activePage === `Suspended (${acceleratorsCount[3] || 0})` && 'suspended'}
                    isSuspendLoading={isSuspendLoading}
                />
            }
            <EditUser
                setEdit={setEditAccelerators}
                ref={userEditRef}
                selectedUser={selectedAccelerator}
                isEditUser={isEditAccelerators}
                type='accelerator'
                handleActionUser={handleSuspendUser}
                isSuspendLoading={isSuspendLoading}
                setSelectUser={setSelectAccelerator}
                setPersonalEmail={setPersonalEmail}
                personalEmail={personalEmail}
                isSaveLoading={isSaveLoading}
                setIsSaveLoading={setIsSaveLoading}
                handleUpdateUser={handleUpdateUser}
                handleUpdateTeamUser={handleUpdateTeamUser}
                reason={reason}
                setReason={setReason}
            />
        </div>
    )
}

export default Accelerators